import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { usePageMeta } from '@/lib/use-document-meta'
import { AuthShell } from '@/components/AuthShell'
import { Modal } from '@/components/Modal'
import { RecoveryCodeDisplay } from '@/components/RecoveryCodeDisplay'
import { Field, inputClass, primaryButtonClass } from '@/components/ui'
import { enableSync } from '@/tracker/sync'

export const Route = createFileRoute('/signup')({ component: SignupPage })

function SignupPage() {
  usePageMeta({
    path: '/signup',
    title: 'Create an account — Lunara',
    description: 'Create a Lunara account for optional encrypted cycle sync across devices.',
    noindex: true,
  })
  const navigate = useNavigate()
  const { register } = useAuth()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null)

  const finish = () => {
    setRecoveryCode(null)
    toast.success('Account created')
    navigate({ to: '/dashboard' })
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    try {
      await register.mutateAsync({ email, password, name })
      // Seal the (empty, by default) local data and turn on E2EE sync. The
      // recovery code is shown ONCE and gated behind "I've saved it". If this
      // fails (e.g. network), we don't block signup — sync can be enabled later.
      try {
        const { recoveryCode } = await enableSync(password)
        setRecoveryCode(recoveryCode)
      } catch {
        toast.error('Sync could not be enabled — you can turn it on from your dashboard.')
        finish()
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sign up failed')
    }
  }

  return (
    <AuthShell
      title="Create an account"
      subtitle="Create an account to sync across devices — even we can’t read it."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="text-primary font-medium">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Name">
          <input
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field
          label="Password"
          hint="At least 8 characters. You'll get a one-time recovery code — save it; losing it (and your password) makes synced data unrecoverable."
        >
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />
        </Field>
        <button type="submit" disabled={register.isPending} className={primaryButtonClass}>
          {register.isPending ? 'Creating…' : 'Create account'}
        </button>
      </form>

      <Modal
        open={recoveryCode !== null}
        onClose={finish}
        title="Save your recovery code"
        disableClose
      >
        {recoveryCode ? <RecoveryCodeDisplay code={recoveryCode} onConfirmed={finish} /> : null}
      </Modal>
    </AuthShell>
  )
}
